import { insurance, providers, specialties } from "./globalSchema.mjs";
import { apikeys, departments, tenantAccessPolicy, tenantEhrConnections, tenants, users } from "./authSchema.mjs";
import { relations, sql } from "drizzle-orm";
import { boolean, date, index, integer, jsonb, pgTable, text, timestamp, unique, uuid } from "drizzle-orm/pg-core";

//#region src/schema/customerSchema.ts
const phoneCallTenantMapping = pgTable("phone_call_tenant_mapping", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	vapiCallId: text("vapi_call_id").unique(),
	retellCallId: text("retell_call_id").unique(),
	vogentCallId: text("vogent_call_id").unique(),
	tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "cascade" }).notNull(),
	createdAt: timestamp("created_at").defaultNow().notNull()
});
/**
* Spruce tenant mapping - NO RLS, used by webhooks to resolve tenant context.
*
* Problem: Spruce webhooks don't include tenant info, and RLS-enabled tables
* can't be queried without tenant context.
*
* Solution: When we send a message, we store (requestId → tenantId). When
* the webhook arrives, we look up tenant by requestId, then upsert
* (conversationId → tenantId) so future messages in that thread can be
* resolved by conversationId alone (including inbound replies).
*/
const spruceTenantMapping = pgTable("spruce_tenant_mapping", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	requestId: text("request_id").unique(),
	conversationId: text("conversation_id").unique(),
	contactId: text("contact_id").unique(),
	tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "cascade" }).notNull(),
	createdAt: timestamp("created_at").defaultNow().notNull()
});
/**
* Whether the quality evaluation scored this call. `quality_metrics` can hold
* only a `callWarranted` verdict (calls that never connected, or whose quality
* evaluation failed), so a non-null column is not enough.
*/
function hasQualityScores(metrics) {
	return !!metrics?.ivrNavigationCorrect;
}
const phoneCalls = pgTable("phone_calls", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "cascade" }).notNull(),
	vapiCallId: text("vapi_call_id").unique(),
	retellCallId: text("retell_call_id").unique(),
	vogentCallId: text("vogent_call_id").unique(),
	transcript: text("transcript"),
	userQuery: text("user_query"),
	summary: text("summary"),
	taskId: uuid("task_id").references(() => tasks.id, { onDelete: "set null" }),
	activityLogExtractedInfo: text("activity_log_extracted_info"),
	callReceiver: text("call_receiver"),
	dialedPhoneNumber: text("dialed_phone_number"),
	successEvaluation: text("success_evaluation"),
	callOutcome: text("call_outcome").$type(),
	structuredExtractedInfo: jsonb("structured_extracted_info").$type(),
	qualityMetrics: jsonb("quality_metrics").$type(),
	qualityEvaluationPassed: boolean("quality_evaluation_passed"),
	recordingMediaUrl: text("recording_media_url"),
	prompt: text("prompt"),
	createdAt: timestamp("created_at").defaultNow().notNull(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date()).notNull(),
	asyncJobId: uuid("async_job_id").references(() => asyncJobs.id, { onDelete: "set null" }),
	phoneAgentVersion: text("phone_agent_version")
}, (_t) => [tenantAccessPolicy]).enableRLS();
const spruceWebhookRegistrations = pgTable("spruce_webhook_registrations", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	webhookUrl: text("webhook_url").notNull(),
	spruceWebhookId: text("spruce_webhook_id"),
	signingSecret: text("signing_secret").notNull(),
	environment: text("environment").notNull(),
	registeredAt: timestamp("registered_at").defaultNow().notNull(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date()).notNull()
}, (table) => [unique().on(table.webhookUrl, table.environment)]);
const processedSpruceConversationItemIds = pgTable("processed_spruce_conversation_item_ids", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "cascade" }),
	conversationItemId: text("conversation_item_id").notNull().unique(),
	processedAt: timestamp("processed_at").defaultNow().notNull()
});
/**
* SuccessEvaluation enum for phone call success evaluation.
*
* For billing purposes:
* - "Collected all relevant information" = billable
* - "Reached human but call incomplete" = not billable
* - "Did not reach a human" = not billable
* - "Office unavailable" = not billable (automated message says office is closed)
* - "Unable to connect" = not billable (phone call failed to connect, no transcript)
*/
let SuccessEvaluation = /* @__PURE__ */ function(SuccessEvaluation) {
	SuccessEvaluation["DidNotReachHuman"] = "Did not reach a human";
	SuccessEvaluation["ReachedHumanCallIncomplete"] = "Reached human but call incomplete";
	SuccessEvaluation["CollectedAllInfo"] = "Collected all relevant information";
	SuccessEvaluation["OfficeUnavailable"] = "Office unavailable";
	SuccessEvaluation["UnableToConnect"] = "Unable to connect";
	return SuccessEvaluation;
}({});
/**
* What a call was for, which decides what counts as each `CallOutcome`.
* Inferred from the call's instructions, since `call_provider` takes free text.
*/
let PhoneCallType = /* @__PURE__ */ function(PhoneCallType) {
	/** Asks the office about itself: insurance, availability, fax, provider. */
	PhoneCallType["ProviderVerification"] = "provider_verification";
	/** Follows up on a sent referral and asks for the visit notes. */
	PhoneCallType["ConsultNotes"] = "consult_notes";
	PhoneCallType["Other"] = "other";
	return PhoneCallType;
}({});
/**
* How a call ended, defined per `PhoneCallType`. Replaces `SuccessEvaluation`,
* which had no slot for a definitive "no" and counted consult-notes calls as
* collected without a commitment to send the notes. Only the three `Answered*`
* outcomes are a call that did its job.
*/
let CallOutcome = /* @__PURE__ */ function(CallOutcome) {
	/** Every requested fact, all favorable; or the notes are coming. */
	CallOutcome["AnsweredOnTrack"] = "answered_on_track";
	/** Consult notes only: the appointment is still ahead, and we have its date. */
	CallOutcome["AnsweredCheckBack"] = "answered_check_back";
	/** A clear answer that stops the referral at this office. */
	CallOutcome["AnsweredBlockerFound"] = "answered_blocker_found";
	CallOutcome["ReachedHumanIncomplete"] = "reached_human_incomplete";
	/** The agent's own mistake kept it from reaching a person. */
	CallOutcome["NoHumanAgentCaused"] = "no_human_agent_caused";
	CallOutcome["NoHumanUnreachable"] = "no_human_unreachable";
	return CallOutcome;
}({});
/**
* Whether a call reached the office we meant to call. A wrong destination is
* a provider directory entry to fix, not the phone agent's failure.
*/
let CallDestination = /* @__PURE__ */ function(CallDestination) {
	/** The office we meant to call, by name or by what it said. */
	CallDestination["Intended"] = "intended";
	/** The office we meant, but it doesn't see the referral's specialty. */
	CallDestination["WrongSpecialty"] = "wrong_specialty";
	/** The office we meant, but the provider we asked for doesn't work there. */
	CallDestination["ProviderNotHere"] = "provider_not_here";
	/** A different healthcare practice. */
	CallDestination["DifferentPractice"] = "different_practice";
	/** A residence, a business that isn't healthcare, or a dead number. */
	CallDestination["NotHealthcare"] = "not_healthcare";
	/** Nothing the other side said shows who it was. */
	CallDestination["Unknown"] = "unknown";
	return CallDestination;
}({});
/**
* External phone calls table - tracks tenant-specific metadata for API calls
*
* Current (MVP): Stores callback URL, webhook secret, nonce for simple webhook forwarding
* Future: Will reference taskId when integrated with task-based agent workflow
*
* Why separate table instead of columns in phone_calls:
* - Keeps phone_calls clean and focused on core call data
* - Easy to query external vs internal calls
* - Extensible for tenant-specific fields (harness config, custom metadata)
* - Normalized design with clear foreign key relationship
*/
const externalPhoneCalls = pgTable("external_phone_calls", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	phoneCallId: uuid("phone_call_id").references(() => phoneCalls.id, { onDelete: "cascade" }).notNull().unique(),
	callbackUrl: text("callback_url").notNull(),
	webhookSecret: text("webhook_secret").notNull(),
	nonce: text("nonce"),
	apiKeyId: text("api_key_id").references(() => apikeys.id, { onDelete: "set null" }),
	successEvaluation: text("success_evaluation", { enum: [
		SuccessEvaluation.DidNotReachHuman,
		SuccessEvaluation.ReachedHumanCallIncomplete,
		SuccessEvaluation.CollectedAllInfo,
		SuccessEvaluation.OfficeUnavailable,
		SuccessEvaluation.UnableToConnect
	] }),
	webhookDeliveredAt: timestamp("webhook_delivered_at"),
	createdAt: timestamp("created_at").defaultNow().notNull(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date()).notNull()
});
let ConsentStatus = /* @__PURE__ */ function(ConsentStatus) {
	ConsentStatus["Unknown"] = "unknown";
	ConsentStatus["Inflight"] = "inflight";
	ConsentStatus["Consented"] = "consented";
	ConsentStatus["Declined"] = "declined";
	return ConsentStatus;
}({});
let SpokenLanguage = /* @__PURE__ */ function(SpokenLanguage) {
	SpokenLanguage["English"] = "English";
	SpokenLanguage["Spanish"] = "Spanish";
	return SpokenLanguage;
}({});
const patients = pgTable("patients", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "cascade" }).notNull(),
	name: text("name").notNull(),
	email: text("email"),
	phoneNumber: text("phone_number"),
	responsiblePartyName: text("responsible_party_name"),
	address: text("address"),
	primaryLanguage: text("primary_language", { enum: [SpokenLanguage.English, SpokenLanguage.Spanish] }),
	dateOfBirth: date("date_of_birth").notNull(),
	spruceContactId: text("spruce_contact_id"),
	textMessageConsent: boolean("text_message_consent"),
	consentRequestStatus: text("consent_request_status", { enum: [
		ConsentStatus.Unknown,
		ConsentStatus.Inflight,
		ConsentStatus.Consented,
		ConsentStatus.Declined
	] }).notNull().default(ConsentStatus.Unknown),
	consentLastRequestedAt: timestamp("consent_last_requested_at"),
	consentRequestCount: integer("consent_request_count").default(0),
	notes: text("notes"),
	createdAt: timestamp("created_at").defaultNow().notNull(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date()).notNull()
}, (t) => [tenantAccessPolicy, index("idx_patients_name_trgm").using("gin", sql`lower(${t.name}) gin_trgm_ops`)]).enableRLS();
let TaskStatus = /* @__PURE__ */ function(TaskStatus) {
	TaskStatus["ActionRequired"] = "Action Required";
	TaskStatus["Completed"] = "Completed";
	TaskStatus["Snoozed"] = "Snoozed";
	return TaskStatus;
}({});
const tasks = pgTable("tasks", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "cascade" }).notNull(),
	title: text("title").notNull(),
	currentStep: text("current_step"),
	status: text("status", { enum: [
		TaskStatus.ActionRequired,
		TaskStatus.Completed,
		TaskStatus.Snoozed
	] }).default(TaskStatus.ActionRequired).notNull(),
	department: text("department").notNull(),
	taskType: text("task_type"),
	priority: text("priority"),
	patientId: uuid("patient_id").references(() => patients.id, { onDelete: "cascade" }).notNull(),
	notes: text("notes"),
	referralId: uuid("referral_id").references(() => referrals.id, { onDelete: "set null" }),
	unsnoozeDate: timestamp("unsnooze_date"),
	createdBy: text("created_by").references(() => users.id),
	assignedTo: text("assigned_to").references(() => users.id, { onDelete: "set null" }),
	assigneeType: text("assignee_type", { enum: ["user", "agent"] }).default("user"),
	lastActivityTimestamp: timestamp("last_activity_timestamp").defaultNow().notNull(),
	createdAt: timestamp("created_at").defaultNow().notNull(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date()).notNull()
}, (t) => [
	tenantAccessPolicy,
	index("idx_tasks_tenant_id_updated_at").on(t.tenantId, t.updatedAt),
	index("idx_tasks_referral_id").on(t.referralId)
]).enableRLS();
let ActivityType = /* @__PURE__ */ function(ActivityType) {
	ActivityType["Error"] = "error";
	/**
	* @deprecated Use PriorAuthResult with payload.success instead
	*/
	ActivityType["PriorAuthCompleted"] = "prior_auth_completed";
	ActivityType["PriorAuthResult"] = "prior_auth_result";
	ActivityType["EligibilityCheckCompleted"] = "eligibility_check_completed";
	ActivityType["EHRComment"] = "ehr_comment";
	ActivityType["Comment"] = "comment";
	ActivityType["ReferralSent"] = "referral_sent";
	ActivityType["PhoneCall"] = "call";
	ActivityType["ConsentUpdate"] = "consent_update";
	ActivityType["SpruceMessageSent"] = "spruce_message_sent";
	ActivityType["SpruceMessageReceived"] = "spruce_message_received";
	ActivityType["SpruceInternalNote"] = "spruce_internal_note";
	ActivityType["SearchProviders"] = "search_providers";
	ActivityType["Unsnooze"] = "unsnooze";
	ActivityType["ReassignToUser"] = "reassign_to_user";
	ActivityType["ReassignToAgent"] = "reassign_to_agent";
	ActivityType["Snooze"] = "snooze";
	ActivityType["TaskCompleted"] = "task_completed";
	return ActivityType;
}({});
/**
* Why the agent or system handed a task to a person. Seeded from the
* product-analytics escalation buckets so the regex and the recorded reason can
* be compared side by side.
*/
let EscalationReason = /* @__PURE__ */ function(EscalationReason) {
	EscalationReason["EligibilityPriorAuth"] = "eligibility_prior_auth";
	EscalationReason["ProviderUnreachable"] = "provider_unreachable";
	EscalationReason["PatientNonResponsive"] = "patient_non_responsive";
	EscalationReason["CannotMessage"] = "cannot_message";
	EscalationReason["OutOfScope"] = "out_of_scope";
	EscalationReason["ConsultNotesNotReceived"] = "consult_notes_not_received";
	EscalationReason["PatientDidNotSchedule"] = "patient_did_not_schedule";
	EscalationReason["PcpNotUpdated"] = "pcp_not_updated";
	EscalationReason["AgentStuck"] = "agent_stuck";
	EscalationReason["Other"] = "other";
	return EscalationReason;
}({});
/** What the agent found when it completed a task. */
let CompletionOutcome = /* @__PURE__ */ function(CompletionOutcome) {
	CompletionOutcome["RecordsReceived"] = "records_received";
	CompletionOutcome["PatientDeclined"] = "patient_declined";
	CompletionOutcome["ClosedAsDuplicate"] = "closed_as_duplicate";
	CompletionOutcome["SentOutsideSkyway"] = "sent_outside_skyway";
	CompletionOutcome["Other"] = "other";
	return CompletionOutcome;
}({});
/** What a snoozed task is waiting for. */
let SnoozeWaitingFor = /* @__PURE__ */ function(SnoozeWaitingFor) {
	SnoozeWaitingFor["PatientReply"] = "patient_reply";
	SnoozeWaitingFor["Consent"] = "consent";
	SnoozeWaitingFor["UpcomingAppointment"] = "upcoming_appointment";
	SnoozeWaitingFor["ConsultNotes"] = "consult_notes";
	SnoozeWaitingFor["ProviderResponse"] = "provider_response";
	SnoozeWaitingFor["PayerResponse"] = "payer_response";
	SnoozeWaitingFor["Other"] = "other";
	return SnoozeWaitingFor;
}({});
/**
* Stored in `reasonDetail` when the agent omitted the reason code or sent one
* outside the enum. The action still runs, so the clinic sees no error.
*/
const REASON_MISSING = "reason_missing";
const activityLogItems = pgTable("activity_log_items", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "cascade" }).notNull(),
	taskId: uuid("task_id").references(() => tasks.id, { onDelete: "cascade" }).notNull(),
	content: text("content").notNull(),
	activityType: text("activity_type", { enum: [
		ActivityType.Error,
		ActivityType.PriorAuthCompleted,
		ActivityType.PriorAuthResult,
		ActivityType.EligibilityCheckCompleted,
		ActivityType.EHRComment,
		ActivityType.Comment,
		ActivityType.ReferralSent,
		ActivityType.PhoneCall,
		ActivityType.ConsentUpdate,
		ActivityType.SpruceMessageSent,
		ActivityType.SpruceMessageReceived,
		ActivityType.SpruceInternalNote,
		ActivityType.SearchProviders,
		ActivityType.Unsnooze,
		ActivityType.ReassignToUser,
		ActivityType.ReassignToAgent,
		ActivityType.Snooze,
		ActivityType.TaskCompleted
	] }),
	createdBy: text("created_by").references(() => users.id),
	mediaItem: jsonb("media_item").$type(),
	payload: jsonb("payload").$type(),
	createdAt: timestamp("created_at").defaultNow().notNull(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date()).notNull()
}, (t) => [
	tenantAccessPolicy,
	index("idx_activity_log_items_tenant_id_updated_at").on(t.tenantId, t.updatedAt),
	index("idx_activity_log_items_task_id_created_at").on(t.taskId, t.createdAt)
]).enableRLS();
const referrals = pgTable("referrals", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "cascade" }).notNull(),
	patientId: uuid("patient_id").references(() => patients.id, { onDelete: "cascade" }),
	createdAt: timestamp("created_at").defaultNow().notNull(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date()).notNull(),
	specialtyId: uuid("specialty_id").references(() => specialties.id, { onDelete: "set null" }),
	context: text("context"),
	parsedReferralId: text("parsed_referral_id"),
	ehrIdentifier: jsonb("ehr_identifier").$type(),
	diagnosisCodes: jsonb("diagnosis_codes").$type(),
	ehrStatus: text("ehr_status")
}, (_t) => [tenantAccessPolicy]).enableRLS();
/** A step a referral reached. Rows are only ever added, never edited. */
let ReferralMilestone = /* @__PURE__ */ function(ReferralMilestone) {
	ReferralMilestone["ConsentRequested"] = "consent_requested";
	ReferralMilestone["ConsentGranted"] = "consent_granted";
	ReferralMilestone["EligibilityConfirmed"] = "eligibility_confirmed";
	ReferralMilestone["EligibilityFailed"] = "eligibility_failed";
	ReferralMilestone["PriorAuthApproved"] = "prior_auth_approved";
	ReferralMilestone["PriorAuthDenied"] = "prior_auth_denied";
	ReferralMilestone["ProviderSelected"] = "provider_selected";
	ReferralMilestone["ReferralSent"] = "referral_sent";
	ReferralMilestone["SentOutsideSkyway"] = "sent_outside_skyway";
	ReferralMilestone["AppointmentScheduled"] = "appointment_scheduled";
	ReferralMilestone["AppointmentAttended"] = "appointment_attended";
	ReferralMilestone["NoShow"] = "no_show";
	ReferralMilestone["RecordsReceived"] = "records_received";
	ReferralMilestone["PatientDeclined"] = "patient_declined";
	ReferralMilestone["ClosedAsDuplicate"] = "closed_as_duplicate";
	ReferralMilestone["Escalated"] = "escalated";
	ReferralMilestone["Other"] = "other";
	return ReferralMilestone;
}({});
let MilestoneRecordedBy = /* @__PURE__ */ function(MilestoneRecordedBy) {
	MilestoneRecordedBy["System"] = "system";
	MilestoneRecordedBy["Agent"] = "agent";
	MilestoneRecordedBy["Coordinator"] = "coordinator";
	MilestoneRecordedBy["Backfill"] = "backfill";
	return MilestoneRecordedBy;
}({});
const referralMilestones = pgTable("referral_milestones", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "cascade" }).notNull(),
	taskId: uuid("task_id").references(() => tasks.id, { onDelete: "cascade" }).notNull(),
	referralId: uuid("referral_id").references(() => referrals.id, { onDelete: "cascade" }).notNull(),
	milestone: text("milestone", { enum: Object.values(ReferralMilestone) }).notNull(),
	detail: text("detail"),
	occurredAt: timestamp("occurred_at").notNull(),
	recordedAt: timestamp("recorded_at").defaultNow().notNull(),
	recordedBy: text("recorded_by", { enum: Object.values(MilestoneRecordedBy) }).notNull(),
	activityLogItemId: uuid("activity_log_item_id").references(() => activityLogItems.id, { onDelete: "cascade" }).notNull()
}, (t) => [
	tenantAccessPolicy,
	unique("uq_referral_milestones_activity_log_item_milestone").on(t.activityLogItemId, t.milestone),
	index("idx_referral_milestones_tenant_referral_occurred_at").on(t.tenantId, t.referralId, t.occurredAt),
	index("idx_referral_milestones_task_id").on(t.taskId)
]).enableRLS();
let AsyncJobStatus = /* @__PURE__ */ function(AsyncJobStatus) {
	AsyncJobStatus["Running"] = "running";
	AsyncJobStatus["Finished"] = "finished";
	AsyncJobStatus["Error"] = "error";
	AsyncJobStatus["TimedOut"] = "timed_out";
	return AsyncJobStatus;
}({});
let JobType = /* @__PURE__ */ function(JobType) {
	JobType["Test"] = "test";
	JobType["ReferralPriorAuth"] = "referral-prior-auth";
	JobType["PullOpenReferrals"] = "pull-open-referrals";
	JobType["AttachRecordsSendReferral"] = "attach-records-send-referral";
	JobType["CheckEligibility"] = "check-eligibility";
	JobType["SearchProviders"] = "search-providers";
	JobType["CallProviders"] = "call-providers";
	JobType["AgentNextStep"] = "agent-next-step";
	JobType["SendSpruceMessage"] = "send-message-to-patient";
	JobType["RecordPatientConsent"] = "record-patient-consent";
	return JobType;
}({});
const asyncJobs = pgTable("async_jobs", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "cascade" }).notNull(),
	jobType: text("job_type", { enum: [
		JobType.Test,
		JobType.ReferralPriorAuth,
		JobType.PullOpenReferrals,
		JobType.AttachRecordsSendReferral,
		JobType.CheckEligibility,
		JobType.SearchProviders,
		JobType.CallProviders,
		JobType.AgentNextStep,
		JobType.SendSpruceMessage,
		JobType.RecordPatientConsent
	] }).notNull(),
	status: text("status", { enum: [
		AsyncJobStatus.Running,
		AsyncJobStatus.Finished,
		AsyncJobStatus.Error,
		AsyncJobStatus.TimedOut
	] }).notNull(),
	jobParams: jsonb("job_params").$type(),
	associatedTaskId: uuid("associated_task_id").references(() => tasks.id, { onDelete: "set null" }),
	associatedDepartmentId: uuid("associated_department_id").references(() => departments.id, { onDelete: "set null" }),
	associatedTenantEhrConnectionId: uuid("associated_tenant_ehr_connection_id").references(() => tenantEhrConnections.id, { onDelete: "set null" }),
	associatedPatientId: uuid("associated_patient_id"),
	associatedSpruceRequestId: text("associated_spruce_request_id"),
	hostedJobId: text("hosted_job_id"),
	decisionStepAsyncJobId: uuid("decision_making_async_job_id"),
	startedTime: timestamp("started_time").defaultNow().notNull(),
	finishedTime: timestamp("finished_time"),
	errorMessage: text("error_message"),
	createdAt: timestamp("created_at").defaultNow().notNull(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date()).notNull()
}, (t) => [
	tenantAccessPolicy,
	index("idx_async_jobs_hosted_job_id").on(t.hostedJobId),
	index("idx_async_jobs_ehr_conn_job_type_finished_time").on(t.associatedTenantEhrConnectionId, t.jobType, t.finishedTime.desc().nullsFirst()),
	index("idx_async_jobs_job_type_running").on(t.jobType).where(sql`status = 'running'`)
]).enableRLS();
const patientInsurance = pgTable("patient_insurance", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "cascade" }).notNull(),
	patientId: uuid("patient_id").notNull().references(() => patients.id, { onDelete: "cascade" }),
	insuranceId: uuid("insurance_id").notNull().references(() => insurance.id),
	insuranceMemberId: text("insurance_member_id"),
	isPrimary: boolean("is_primary"),
	isExpired: boolean("is_expired"),
	insuranceCardImageUrls: jsonb("insurance_card_image_urls").$type(),
	pcpId: uuid("pcp_id").references(() => providers.id, { onDelete: "set null" }),
	validatedAt: timestamp("validated_at"),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date()).notNull()
}, (_t) => [tenantAccessPolicy]).enableRLS();
const patientInsuranceRelations = relations(patientInsurance, ({ one }) => ({
	tenant: one(tenants, {
		fields: [patientInsurance.tenantId],
		references: [tenants.id]
	}),
	patient: one(patients, {
		fields: [patientInsurance.patientId],
		references: [patients.id]
	}),
	insurance: one(insurance, {
		fields: [patientInsurance.insuranceId],
		references: [insurance.id]
	}),
	pcp: one(providers, {
		fields: [patientInsurance.pcpId],
		references: [providers.id]
	})
}));
const patientsRelations = relations(patients, ({ one, many }) => ({
	tenant: one(tenants, {
		fields: [patients.tenantId],
		references: [tenants.id]
	}),
	patientInsurance: many(patientInsurance),
	referrals: many(referrals),
	tasks: many(tasks)
}));
const phoneCallsRelations = relations(phoneCalls, ({ one }) => ({
	tenant: one(tenants, {
		fields: [phoneCalls.tenantId],
		references: [tenants.id]
	}),
	task: one(tasks, {
		fields: [phoneCalls.taskId],
		references: [tasks.id]
	}),
	asyncJob: one(asyncJobs, {
		fields: [phoneCalls.asyncJobId],
		references: [asyncJobs.id]
	}),
	externalPhoneCall: one(externalPhoneCalls, {
		fields: [phoneCalls.id],
		references: [externalPhoneCalls.phoneCallId]
	})
}));
const externalPhoneCallsRelations = relations(externalPhoneCalls, ({ one }) => ({
	phoneCall: one(phoneCalls, {
		fields: [externalPhoneCalls.phoneCallId],
		references: [phoneCalls.id]
	}),
	apiKey: one(apikeys, {
		fields: [externalPhoneCalls.apiKeyId],
		references: [apikeys.id]
	})
}));
const tasksRelations = relations(tasks, ({ one, many }) => ({
	tenant: one(tenants, {
		fields: [tasks.tenantId],
		references: [tenants.id]
	}),
	assignee: one(users, {
		fields: [tasks.assignedTo],
		references: [users.id]
	}),
	patient: one(patients, {
		fields: [tasks.patientId],
		references: [patients.id]
	}),
	activityLogItems: many(activityLogItems),
	phoneCalls: many(phoneCalls),
	associatedJobs: many(asyncJobs),
	referral: one(referrals, {
		fields: [tasks.referralId],
		references: [referrals.id]
	}),
	department: one(departments, {
		fields: [tasks.department],
		references: [departments.name]
	})
}));
const activityLogItemsRelations = relations(activityLogItems, ({ one }) => ({
	tenant: one(tenants, {
		fields: [activityLogItems.tenantId],
		references: [tenants.id]
	}),
	task: one(tasks, {
		fields: [activityLogItems.taskId],
		references: [tasks.id]
	}),
	createdBy: one(users, {
		fields: [activityLogItems.createdBy],
		references: [users.id]
	})
}));
const referralsRelations = relations(referrals, ({ one, many }) => ({
	tenant: one(tenants, {
		fields: [referrals.tenantId],
		references: [tenants.id]
	}),
	patient: one(patients, {
		fields: [referrals.patientId],
		references: [patients.id]
	}),
	specialty: one(specialties, {
		fields: [referrals.specialtyId],
		references: [specialties.id]
	}),
	tasks: many(tasks)
}));
const departmentsRelations = relations(departments, ({ one, many }) => ({
	tenant: one(tenants, {
		fields: [departments.tenantId],
		references: [tenants.id]
	}),
	tenantEhrConnection: one(tenantEhrConnections, {
		fields: [departments.tenantEhrConnectionId],
		references: [tenantEhrConnections.id]
	}),
	requestingProvider: one(providers, {
		fields: [departments.requestingProviderId],
		references: [providers.id]
	}),
	users: many(users),
	associatedJobs: many(asyncJobs)
}));
const tenantEhrConnectionsRelations = relations(tenantEhrConnections, ({ one, many }) => ({
	tenant: one(tenants, {
		fields: [tenantEhrConnections.tenantId],
		references: [tenants.id]
	}),
	departments: many(departments),
	associatedJobs: many(asyncJobs)
}));
const asyncJobsRelations = relations(asyncJobs, ({ one }) => ({
	tenant: one(tenants, {
		fields: [asyncJobs.tenantId],
		references: [tenants.id]
	}),
	task: one(tasks, {
		fields: [asyncJobs.associatedTaskId],
		references: [tasks.id]
	}),
	department: one(departments, {
		fields: [asyncJobs.associatedDepartmentId],
		references: [departments.id]
	}),
	tenantEhrConnection: one(tenantEhrConnections, {
		fields: [asyncJobs.associatedTenantEhrConnectionId],
		references: [tenantEhrConnections.id]
	}),
	patient: one(patients, {
		fields: [asyncJobs.associatedPatientId],
		references: [patients.id]
	}),
	decisionStepAsyncJob: one(asyncJobs, {
		fields: [asyncJobs.decisionStepAsyncJobId],
		references: [asyncJobs.id]
	})
}));
const processedSpruceConversationItemIdsRelations = relations(processedSpruceConversationItemIds, ({ one }) => ({ tenant: one(tenants, {
	fields: [processedSpruceConversationItemIds.tenantId],
	references: [tenants.id]
}) }));

//#endregion
export { ActivityType, AsyncJobStatus, CallDestination, CallOutcome, CompletionOutcome, ConsentStatus, EscalationReason, JobType, MilestoneRecordedBy, PhoneCallType, REASON_MISSING, ReferralMilestone, SnoozeWaitingFor, SpokenLanguage, SuccessEvaluation, TaskStatus, activityLogItems, activityLogItemsRelations, asyncJobs, asyncJobsRelations, departmentsRelations, externalPhoneCalls, externalPhoneCallsRelations, hasQualityScores, patientInsurance, patientInsuranceRelations, patients, patientsRelations, phoneCallTenantMapping, phoneCalls, phoneCallsRelations, processedSpruceConversationItemIds, processedSpruceConversationItemIdsRelations, referralMilestones, referrals, referralsRelations, spruceTenantMapping, spruceWebhookRegistrations, tasks, tasksRelations, tenantEhrConnectionsRelations };